// =============================================================================
// DATA LOADER SERVICE
// =============================================================================
// Service for loading page data from various sources with caching

import type { ContentComponent, NavItem, SEOData } from '@/types';
import { dataCache } from './dataCache';
import { isFirebaseConfigured } from './firestore';
import { contentStorage } from './contentStorage';
import { addComponentOrder, sortComponentsByOrder } from '@/utils/serialization';

export type DataSource = 'cache' | 'local' | 'firebase' | 'published';

export interface PageDataBundle {
  pageId: string;
  components: ContentComponent[];
  navigation?: {
    navItems?: NavItem[];
    footerLinks?: NavItem[];
    socialLinks?: NavItem[];
  };
  seo?: SEOData;
  source: DataSource;
}

/**
 * Data loader service that can load from multiple sources with caching
 * Priority: Cache -> Firebase Storage (published) -> Local Files
 */
export class DataLoaderService {
  private preferredSource: DataSource = isFirebaseConfigured ? 'published' : 'local';
  private useCache: boolean = true;

  /**
   * Set the preferred data source
   */
  setPreferredSource(source: DataSource): void {
    this.preferredSource = source;
  }

  /**
   * Get the current preferred source
   */
  getPreferredSource(): DataSource {
    return this.preferredSource;
  }

  /**
   * Enable or disable caching
   */
  setUseCache(useCache: boolean): void {
    this.useCache = useCache;
  }

  /**
   * Clear cache for a specific page
   */
  clearCache(pageId: string): void {
    dataCache.remove(pageId);
  }

  /**
   * Clear all cached pages
   */
  clearAllCache(): void {
    dataCache.clearAll();
  }

  /**
   * Load page data from Firebase Storage (published version for live site)
   * Path: content/published/{pageId}.json
   */
  async loadFromPublished(pageId: string): Promise<PageDataBundle | null> {
    if (!isFirebaseConfigured) {
      return this.loadFromLocal(pageId);
    }

    try {
      const pageData = await contentStorage.loadPage(pageId);
      
      if (!pageData) {
        return this.loadFromLocal(pageId);
      }

      // Load navigation from local files (only stored in home page)
      let navigation: PageDataBundle['navigation'];
      if (pageId === 'home') {
        try {
          const homeModule = await import('@/data/homeContent');
          navigation = {
            navItems: homeModule.navItems,
            footerLinks: homeModule.footerLinks,
            socialLinks: homeModule.socialLinks,
          };
        } catch (error) {
          // Could not load navigation from local files
        }
      }

      // Ensure components have order and are sorted
      const orderedComponents = sortComponentsByOrder(addComponentOrder(pageData.components));

      return {
        pageId: pageData.pageId,
        components: orderedComponents,
        navigation,
        seo: pageData.seo,
        source: 'published',
      };
    } catch (error) {
      return this.loadFromLocal(pageId);
    }
  }

  /**
   * Alias for loadFromPublished
   */
  async loadFromFirebase(pageId: string): Promise<PageDataBundle | null> {
    return this.loadFromPublished(pageId);
  }

  /**
   * Load page data from local TypeScript files
   */
  async loadFromLocal(pageId: string): Promise<PageDataBundle | null> {
    try {
      let components: ContentComponent[] = [];
      let navigation: PageDataBundle['navigation'];
      
      switch (pageId) {
        case 'home': {
          const homeModule = await import('@/data/homeContent');
          components = homeModule.homePageComponents || [];
          navigation = {
            navItems: homeModule.navItems,
            footerLinks: homeModule.footerLinks,
            socialLinks: homeModule.socialLinks,
          };
          break;
        }
        case 'terms':
        case 'privacy': {
          const legalModule = await import('@/data/legalPageContent');
          components = pageId === 'terms' 
            ? legalModule.termsPageComponents || []
            : legalModule.privacyPageComponents || [];
          break;
        }
        default:
          // Unknown page ID
          return null;
      }

      // Ensure components have order and are sorted
      const orderedComponents = sortComponentsByOrder(addComponentOrder(components));

      return {
        pageId,
        components: orderedComponents,
        navigation,
        source: 'local',
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Load page data with caching
   * Priority: Cache -> Firebase Storage -> Local Files
   */
  async loadPage(pageId: string): Promise<PageDataBundle | null> {
    try {
      // Check cache first
      if (this.useCache) {
        const cached = dataCache.get(pageId);
        if (cached) return cached;
      }

      // Load from preferred source
      let result: PageDataBundle | null = null;
      
      switch (this.preferredSource) {
        case 'published':
        case 'firebase':
          result = await this.loadFromPublished(pageId);
          break;
        case 'local':
          result = await this.loadFromLocal(pageId);
          break;
        case 'cache':
          result = await this.loadFromPublished(pageId);
          break;
      }

      if (result && this.useCache) {
        dataCache.set(pageId, result);
      }

      return result;
    } catch (error) {
      return null;
    }
  }

  /**
   * Preload multiple pages into cache
   */
  async preloadPages(pageIds: string[]): Promise<void> {
    if (!this.useCache) return;
    await dataCache.preload(pageIds, (pageId) => this.loadPageWithoutCache(pageId));
  }

  /**
   * Load page without using cache (for preloading and cache refresh)
   */
  private async loadPageWithoutCache(pageId: string): Promise<PageDataBundle | null> {
    const wasUsingCache = this.useCache;
    this.useCache = false;
    
    try {
      return await this.loadPage(pageId);
    } finally {
      this.useCache = wasUsingCache;
    }
  }

}

// Export singleton instance
export const dataLoader = new DataLoaderService();

// Export class for custom instances
export default DataLoaderService;

